import { ClientInput } from 'admin/inputs/ClientInput';
import { DateInput } from 'admin/inputs/DateInput';
import { ExpectedAmountInput } from 'admin/inputs/ExpectedAmountInput';
import { InstitutionInput } from 'admin/inputs/InstitutionInput';
import { ProductStatusInput } from 'admin/products/inputs/ProductStatusInput';
import { PERIOD_TYPE_CHOICES } from 'admin/utils';
import {
  PropertyInsuranceInsuranceRiskType,
  PropertyInsuranceInsuranceType,
  PropertyInsuranceInsuredPropertyType,
} from 'generated/graphql';
import { RichTextInput } from 'ra-input-rich-text';
import {
  ArrayInput,
  Create,
  FormTab,
  getRecordFromLocation,
  NumberInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
} from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { PropertyInsuranceInsuranceRiskTypeTranslations } from 'translations/enums';

// TODO: generalize components

export const PropertyInsuranceProductCreate: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const record = getRecordFromLocation(location);
  return (
    <Create>
      <TabbedForm>
        <FormTab label="Základné info">
          <ClientInput disabled={!!record?.clientId} label="Klient" source="clientId" />
          <InstitutionInput source="institutionId" />
          <TextInput label="Vlastná inštitúcia" source="institutionName" sx={{ maxWidth: '40em', width: '100%' }} />
          <TextInput label="Názov produktu" source="name" sx={{ maxWidth: '40em', width: '100%' }} />
        </FormTab>

        <FormTab label="Zmluva">
          <TextInput label="Číslo zmluvy" source="contractNumber" sx={{ maxWidth: '40em', width: '100%' }} />
          <DateInput label="Začiatok poistenia" source="contractSignDate" />
          <DateInput label="Platnosť poistenia do" source="contractEndDate" />
        </FormTab>

        <FormTab label="Platby">
          <ExpectedAmountInput label="Suma poistného" source="paymentPeriod.expectedAmount.amount" />
          <TextInput source="paymentPeriod.expectedAmount.currency" style={{ display: 'none' }} value="EUR" />
          <NumberInput label="Opakovať každých" source="paymentPeriod.periodCount" />
          <SelectInput choices={PERIOD_TYPE_CHOICES} label="" source="paymentPeriod.periodTypeCode" />
          <DateInput label="Nasledujúca platba" source="paymentPeriod.expectedDate" />
        </FormTab>

        <FormTab label="Údaje pre platbu">
          <TextInput
            label="Variabilný symbol"
            source="paymentInfo.variableSymbol"
            sx={{ maxWidth: '40em', width: '100%' }}
          />
          <TextInput
            label="Špecifický symbol"
            source="paymentInfo.specificSymbol"
            sx={{ maxWidth: '40em', width: '100%' }}
          />
          <TextInput
            label="Konštantný symbol"
            source="paymentInfo.constantSymbol"
            sx={{ maxWidth: '40em', width: '100%' }}
          />
          <TextInput label="IBAN" source="paymentInfo.ibanForPayment" sx={{ maxWidth: '40em', width: '100%' }} />
        </FormTab>

        <FormTab label="Poistená nehnuteľnosť">
          <SelectInput
            choices={[
              { id: PropertyInsuranceInsuredPropertyType.House, name: 'Dom' },
              { id: PropertyInsuranceInsuredPropertyType.Apartment, name: 'Byt' },
              { id: PropertyInsuranceInsuredPropertyType.RecreationalFacility, name: 'Rekreačný objekt' },
            ]}
            label="Typ"
            source="insuredProperty.type"
          />
          <TextInput label="Adresa" source="insuredProperty.address" sx={{ maxWidth: '40em', width: '100%' }} />
          <NumberInput label="Hodnota nehnuteľnosti" source="insuredProperty.propertyValue.amount" />
          <NumberInput label="Hodnota domácnosti" source="insuredProperty.householdValue.amount" />
        </FormTab>

        <FormTab label="Základné poistenia">
          <ArrayInput label="Základné poistenia" source="basicInsurances">
            <SimpleFormIterator inline>
              <SelectInput
                choices={[
                  { id: PropertyInsuranceInsuranceType.Property, name: 'Poistenie nehnuteľnosti' },
                  { id: PropertyInsuranceInsuranceType.Household, name: 'Poistenie domácnosti' },
                ]}
                helperText={false}
                label="Typ"
                source="type"
              />
              <NumberInput helperText={false} label="Poistná suma" source="insuredSum.amount" />
              <NumberInput helperText={false} label="Poistné" source="annualCost.amount" />
              <ArrayInput label="Poistené riziká" source="insuranceRisks">
                <SimpleFormIterator inline>
                  <SelectInput
                    choices={[
                      PropertyInsuranceInsuranceRiskType.NaturalDisaster,
                      PropertyInsuranceInsuranceRiskType.WaterDamage,
                      PropertyInsuranceInsuranceRiskType.Explosion,
                      PropertyInsuranceInsuranceRiskType.SmokeDamage,
                      PropertyInsuranceInsuranceRiskType.Theft,
                      PropertyInsuranceInsuranceRiskType.Vandalism,
                      PropertyInsuranceInsuranceRiskType.Liability,
                      PropertyInsuranceInsuranceRiskType.Indexation,
                      PropertyInsuranceInsuranceRiskType.Other,
                    ].map((type) => ({
                      id: type,
                      name: PropertyInsuranceInsuranceRiskTypeTranslations(t)[type],
                    }))}
                    helperText={false}
                    label="Typ rizika"
                    source="type"
                  />
                  <TextInput
                    helperText={false}
                    label="Názov rizika"
                    source="name"
                    sx={{ maxWidth: '40em', width: '100%' }}
                  />
                  <NumberInput helperText={false} label="Poistná suma" source="insuredSum.amount" />
                  <NumberInput helperText={false} label="Poistné" source="annualCost.amount" />
                </SimpleFormIterator>
              </ArrayInput>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>

        <FormTab label="Pripoistenia">
          <ArrayInput label="Pripoistenia" source="otherInsurances">
            <SimpleFormIterator inline>
              <TextInput
                helperText={false}
                label="Názov pripoistenia"
                source="name"
                sx={{ maxWidth: '40em', width: '100%' }}
              />
              <NumberInput helperText={false} label="Poistná suma" source="insuredSum.amount" />
              <NumberInput helperText={false} label="Poistné" source="annualCost.amount" />
              <ArrayInput label="Poistené riziká" source="insuranceRisks">
                <SimpleFormIterator inline>
                  <SelectInput
                    choices={[
                      PropertyInsuranceInsuranceRiskType.NaturalDisaster,
                      PropertyInsuranceInsuranceRiskType.WaterDamage,
                      PropertyInsuranceInsuranceRiskType.Explosion,
                      PropertyInsuranceInsuranceRiskType.SmokeDamage,
                      PropertyInsuranceInsuranceRiskType.Theft,
                      PropertyInsuranceInsuranceRiskType.Vandalism,
                      PropertyInsuranceInsuranceRiskType.Liability,
                      PropertyInsuranceInsuranceRiskType.Indexation,
                      PropertyInsuranceInsuranceRiskType.Other,
                    ].map((type) => ({
                      id: type,
                      name: PropertyInsuranceInsuranceRiskTypeTranslations(t)[type],
                    }))}
                    helperText={false}
                    label="Typ rizika"
                    source="type"
                  />
                  <TextInput
                    helperText={false}
                    label="Názov rizika"
                    source="name"
                    sx={{ maxWidth: '40em', width: '100%' }}
                  />
                  <NumberInput helperText={false} label="Poistná suma" source="insuredSum.amount" />
                  <NumberInput helperText={false} label="Poistné" source="annualCost.amount" />
                </SimpleFormIterator>
              </ArrayInput>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>

        <FormTab label="Poistník">
          <TextInput label="Meno" source="insuringPerson.name" />
          <TextInput label="Meno" source="insuringPerson.surname" />
        </FormTab>

        <FormTab label="Status">
          <ProductStatusInput label="Status" source="status" />
        </FormTab>

        <FormTab label="Poznámka">
          <RichTextInput
            label="Poznámka"
            source="note"
            sx={{ '& .tiptap': { minHeight: '20em' }, maxWidth: '40em', width: '100%' }}
          />
        </FormTab>
      </TabbedForm>
    </Create>
  );
};
